import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Eye } from 'lucide-react';
import { BeatCluster } from '../types';
import { BEAT_LABEL_NAMES } from '../review';

const TemplatePlot: React.FC<{ samples?: number[] }> = ({ samples }) => {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !samples?.length) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const lo = Math.min(...samples);
    const hi = Math.max(...samples);
    const span = hi - lo || 1;
    ctx.strokeStyle = '#22d3ee';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    samples.forEach((v, i) => {
      const x = (i / (samples.length - 1)) * rect.width;
      const y = rect.height - 4 - ((v - lo) / span) * (rect.height - 8);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
  }, [samples]);
  return <canvas ref={ref} className="h-16 w-full rounded bg-slate-950" />;
};

const RoleSelect: React.FC<{ cluster: BeatCluster; disabled: boolean; onPick: (label: string) => void }> = ({
  cluster, disabled, onPick,
}) => (
  <select
    disabled={disabled}
    value={cluster.label}
    onChange={(e) => onPick(e.target.value)}
    className="rounded border border-slate-700 bg-slate-950 px-1 py-0.5 text-slate-200"
  >
    {Object.entries(BEAT_LABEL_NAMES).map(([k, name]) => (
      <option key={k} value={k}>{name}</option>
    ))}
  </select>
);

interface ClusterGalleryProps {
  clusters: BeatCluster[];
  disabled: boolean;
  onRelabel: (cluster: BeatCluster, label: string) => void;
}

/** Cards big enough to actually judge a beat's shape are only worth it for the clusters that cover
 * most of the study; a real recording produced 200 of them, most a long tail of a few beats each. */
const CARD_COUNT = 8;

/**
 * Morphology templates (§7.2 step 3). Relabelling moves every member beat, so the scope is shown and
 * confirmed before anything is sent — and the server refuses if the count it was shown is stale.
 *
 * Split into cards (the few clusters worth inspecting visually) and a compact table for the rest —
 * 200 flat cards, as a real recording produced, is not reviewable (2026-09-14, from real use).
 */
export const ClusterGallery: React.FC<ClusterGalleryProps> = ({ clusters, disabled, onRelabel }) => {
  const [pending, setPending] = useState<{ cluster: BeatCluster; label: string } | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Server already orders by member_count desc; defend against it not being the case.
  const ordered = useMemo(() => [...clusters].sort((a, b) => b.member_count - a.member_count), [clusters]);
  const cards = ordered.slice(0, CARD_COUNT);
  const rest = ordered.slice(CARD_COUNT);
  const totalBeats = ordered.reduce((n, c) => n + c.member_count, 0) || 1;
  const cardBeats = cards.reduce((n, c) => n + c.member_count, 0);

  return (
    <div className="glass-panel space-y-3 rounded-2xl p-4">
      <div className="flex items-center justify-between text-xs">
        <span className="font-bold uppercase tracking-wide text-white">Beat morphology clusters</span>
        <span className="text-slate-400">{clusters.length} templates</span>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {cards.map((c) => (
          <div key={c.id} className="space-y-1.5 rounded-xl border border-slate-800 bg-slate-900/70 p-2.5 text-xs">
            <TemplatePlot samples={c.template_samples} />
            <div className="flex items-center justify-between">
              <RoleSelect cluster={c} disabled={disabled} onPick={(label) => setPending({ cluster: c, label })} />
              <span className="mono text-slate-300">{c.member_count.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>{(c.confidence * 100).toFixed(0)}% conf.</span>
              {c.engine_label && c.engine_label !== c.label && <span className="text-amber-400">was {BEAT_LABEL_NAMES[c.engine_label]}</span>}
            </div>
          </div>
        ))}
      </div>

      {rest.length > 0 && (
        <div>
          <button
            onClick={() => setShowAll((v) => !v)}
            className="flex w-full items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2 text-xs text-slate-300 hover:border-slate-600"
          >
            {showAll ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {showAll ? 'Hide' : 'Show'} the other {rest.length} clusters
            <span className="mono text-slate-500">
              — {(100 - (100 * cardBeats) / totalBeats).toFixed(1)}% of beats, mostly the noise/outlier tail
            </span>
          </button>
          {showAll && (
            <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-800">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 border-b border-slate-800 bg-slate-900 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="p-2">Label</th>
                    <th className="p-2 text-right">Beats</th>
                    <th className="p-2 text-right">Conf.</th>
                    <th className="p-2 text-right">Shape</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {rest.map((c) => (
                    <React.Fragment key={c.id}>
                      <tr className="hover:bg-slate-900/60">
                        <td className="p-2">
                          <RoleSelect cluster={c} disabled={disabled} onPick={(label) => setPending({ cluster: c, label })} />
                          {c.engine_label && c.engine_label !== c.label && (
                            <span className="ml-1.5 text-[10px] text-amber-400">was {BEAT_LABEL_NAMES[c.engine_label]}</span>
                          )}
                        </td>
                        <td className="mono p-2 text-right text-slate-300">{c.member_count.toLocaleString()}</td>
                        <td className="mono p-2 text-right text-slate-400">{(c.confidence * 100).toFixed(0)}%</td>
                        <td className="p-2 text-right">
                          <button onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)} className="text-slate-400 hover:text-cyan-300">
                            <Eye className="inline h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                      {expandedRow === c.id && (
                        <tr>
                          <td colSpan={4} className="p-2"><TemplatePlot samples={c.template_samples} /></td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {pending && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-800 bg-amber-950/60 p-3 text-xs text-amber-200">
          <span>
            Relabel <strong>{pending.cluster.member_count.toLocaleString()} beats</strong> from{' '}
            {BEAT_LABEL_NAMES[pending.cluster.label]} to {BEAT_LABEL_NAMES[pending.label]}? Undo is available.
          </span>
          <span className="flex gap-2">
            <button className="rounded border border-slate-600 px-2 py-1" onClick={() => setPending(null)}>Cancel</button>
            <button
              className="rounded border border-amber-600 bg-amber-800 px-2 py-1 font-semibold text-white"
              onClick={() => {
                onRelabel(pending.cluster, pending.label);
                setPending(null);
              }}
            >
              Relabel {pending.cluster.member_count.toLocaleString()} beats
            </button>
          </span>
        </div>
      )}
    </div>
  );
};
